import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'context/LanguageContext';
import RelatedProducts from './RelatedProducts';
import QuantitySelector from './QuantitySelector';
import StockStatus from './StockStatus';
import PricingSection from './PricingSection';
import ProductHeader from './ProductHeader';
import ProductBadges from './ProductBadges';
import SizeSelector from './SizeSelector';
import ColorSelector from './ColorSelector';
import AddToCart from './AddToCartButton';
import ProductImage from './ProductImage';
import { useCart } from 'context/CartContext';
import { buildStorageUrl } from 'config/clientConfig';
import { getStockPolicy } from 'utils/stockPolicy';

const ProductDetailsCard = ({ product, relatedProducts }) => {
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);

  const { addToCart } = useCart();
  const { language } = useTranslation();
  const navigate = useNavigate();
  const isRTL = language === 'ar';

  if (!product) return null;

  const { 
    name, description, category, barcode,
    selling_price, discount, is_offer, image, sizes, colors
  } = product;

  const hasSizes = Array.isArray(sizes) && sizes.length > 0;
  const hasColors = Array.isArray(colors) && colors.length > 0;
  const stockPolicy = getStockPolicy(product);
  const { quantity: stockQty, isTracked, isLowStock, isOutOfStock, canAddToCartByStock } = stockPolicy;

  const formatPrice = (priceInCents) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(priceInCents);

  const isVariantSelectionValid =
    (!hasSizes || Boolean(selectedSize)) &&
    (!hasColors || Boolean(selectedColor));
  const canAddToCart = isVariantSelectionValid && canAddToCartByStock;

  const handleAddToCart = (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (!canAddToCartByStock) {
      return;
    }
    
    if (hasSizes && !selectedSize) {
      alert('Please select a size before adding to cart.');
      return;
    }
    if (hasColors && !selectedColor) {
      alert('Please select a color before adding to cart.');
      return;
    }

    const item = { ...product };
    if (hasSizes) item.selectedSize = selectedSize;
    if (hasColors) item.selectedColor = selectedColor;

    addToCart(item, quantity);
    
    // Navigate to homepage after successfully adding to cart
    navigate('/');
  };

  const images = image
    ? [
        buildStorageUrl(
          /^https?:\/\//i.test(String(image)) ? String(image) : `images/${image}`
        ),
      ]
    : [];

  return (
    <>
      <div className="bg-white w-full" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-8 lg:max-w-4xl lg:mx-auto lg:py-8">
            {/* Left: Image + Badges */}
            <div className="space-y-4">
              {/* Desktop: Sticky container */}
              <div className="lg:sticky lg:top-6">
                <div className="relative w-full h-96 lg:h-80 bg-gray-100 rounded-lg overflow-hidden">
                  <ProductImage
                    image={images[currentImageIndex]}
                    productName={name}
                  />
                  <ProductBadges
                    isOffer={is_offer}
                    discount={discount}
                    quantity={stockQty}
                    isLowStock={isLowStock}
                  />
                </div>
                
                {/* Desktop: Image Thumbnails */}
                {images.length > 1 && (
                  <div className="hidden lg:flex gap-3 mt-6 justify-center">
                    {images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                          currentImageIndex === idx
                            ? 'border-blue-500 shadow-md'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={img}
                          alt={`${name} thumbnail ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right: Details */}
            <div className="flex flex-col justify-between space-y-6">
              <div className="space-y-6">
                {/* Desktop: Add border bottom */}
                <div className="lg:border-b lg:border-gray-200 lg:pb-6">
                  <ProductHeader
                    name={name}
                    description={description}
                    category={category}
                    barcode={barcode}
                  />
                </div>

                {/* Desktop: Highlight pricing */}
                <div className="lg:bg-gradient-to-br lg:from-gray-50 lg:to-gray-100 lg:rounded-xl lg:p-5 lg:border lg:border-gray-200">
                  <PricingSection
                    sellingPrice={selling_price}
                    discount={discount}
                    formatPrice={formatPrice}
                  />
                </div>

                <StockStatus
                  quantity={stockQty}
                  isTracked={isTracked}
                  isLowStock={isLowStock}
                  isOutOfStock={isOutOfStock}
                />

                {hasSizes && (
                  <SizeSelector
                    sizes={sizes}
                    selectedSize={selectedSize}
                    onSizeSelect={setSelectedSize}
                  />
                )}

                {hasColors && (
                  <ColorSelector
                  colors={colors}
                  selectedColor={selectedColor}
                  onColorSelect={setSelectedColor}
                  />
                )}

                <QuantitySelector
                  quantity={quantity}
                  setQuantity={setQuantity}
                  maxQuantity={isTracked ? stockQty : undefined}
                  pricePerUnit={selling_price}
                  formatPrice={formatPrice}
                />
              </div>

              {/* Desktop: Sticky button with border */}
              <div className="lg:pt-6 lg:border-t lg:border-gray-200 lg:sticky lg:bottom-0 lg:bg-white">
                <AddToCart
                  product={product}
                  handleOnclick={handleAddToCart}
                  disabled={!canAddToCart}
                />
              </div>
            </div>
          </div>

          {/* Related Products inside card on mobile, outside on desktop */}
          {relatedProducts && relatedProducts.length > 0 && (
            <div className="px-6 pb-6 lg:hidden">
              <RelatedProducts relatedProducts={relatedProducts} />
            </div>
          )}
        </div>

        {/* Related Products - Desktop only (outside main card) */}
        {relatedProducts && relatedProducts.length > 0 && (
          <div className="hidden lg:block lg:max-w-4xl lg:mx-auto lg:py-8">
            <RelatedProducts relatedProducts={relatedProducts} />
          </div>
        )}
    </>
  );
};

export default ProductDetailsCard;
